import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users, UserCheck, UserX, FileText, QrCode, ScanLine, MousePointerClick, AlertTriangle,
  TrendingUp, BarChart3, Percent, Mail, ArrowUpRight,
} from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/growth").then((r) => setGrowth(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const cards = [
    { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Active Users", value: stats?.active_users || 0, icon: UserCheck, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Grace Period", value: stats?.grace_users || 0, icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/10" },
    { label: "Suspended", value: stats?.suspended_users || 0, icon: UserX, color: "text-red-400", bg: "bg-red-500/10" },
    { label: "Total Profiles", value: stats?.total_profiles || 0, icon: FileText, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "QR Codes", value: stats?.total_qrcodes || 0, icon: QrCode, color: "text-violet-400", bg: "bg-violet-500/10" },
    { label: "Total Scans", value: stats?.total_scans || 0, icon: ScanLine, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    { label: "Link Clicks", value: stats?.total_link_clicks || 0, icon: MousePointerClick, color: "text-pink-400", bg: "bg-pink-500/10" },
  ];

  const funnel = growth?.funnel || {};
  const funnelSteps = [
    { key: "landing_page_visit", label: "Landing Views", icon: BarChart3, color: "text-blue-400" },
    { key: "cta_click", label: "CTA Clicks", icon: ArrowUpRight, color: "text-violet-400" },
    { key: "signup_success", label: "Signups", icon: UserCheck, color: "text-emerald-400" },
    { key: "onboarding_complete", label: "Onboarding Done", icon: TrendingUp, color: "text-amber-400" },
  ];

  return (
    <div className="animate-fade-in space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Admin Dashboard</h1>
        <p className="text-muted-foreground text-sm">Platform-wide overview</p>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
        {cards.map((c) => (
          <Card key={c.label} className="card-hover">
            <CardContent className="flex items-center gap-4 p-5">
              <div className={`p-2.5 rounded-lg ${c.bg} ${c.color}`}>
                <c.icon size={20} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{c.value}</p>
                <p className="text-xs text-muted-foreground">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Growth & Conversion */}
      {growth && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion Funnel */}
          <Card className="card-hover" data-testid="admin-funnel-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
                <TrendingUp size={16} className="text-amber-400" /> Marketing Funnel
                {growth.conversion_rate > 0 && (
                  <Badge variant="secondary" className="text-[10px] gap-1 font-normal ml-auto">
                    <Percent size={10} /> {growth.conversion_rate}% conversion
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {funnelSteps.map((step) => {
                const count = funnel[step.key] || 0;
                const maxCount = Math.max(...funnelSteps.map((s) => funnel[s.key] || 0), 1);
                const pct = (count / maxCount) * 100;
                return (
                  <div key={step.key} className="flex items-center gap-3" data-testid={`funnel-${step.key}`}>
                    <step.icon size={16} className={`${step.color} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">{step.label}</span>
                        <span className="text-xs text-muted-foreground font-mono">{count}</span>
                      </div>
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {growth.conversion_rate > 0 && (
                <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/30">
                  Landing → Signup conversion: <span className="font-semibold text-foreground">{growth.conversion_rate}%</span>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Daily Signups */}
          <Card className="card-hover" data-testid="admin-signups-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
                <Users size={16} className="text-emerald-400" /> Daily Signups (30d)
                {growth.contact_submissions > 0 && (
                  <Badge variant="secondary" className="text-[10px] gap-1 font-normal ml-auto">
                    <Mail size={10} /> {growth.contact_submissions} contacts
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {growth.daily_signups?.length > 0 ? (
                <div className="space-y-1.5">
                  {growth.daily_signups.slice(-14).map((d) => {
                    const maxD = Math.max(...growth.daily_signups.map((x) => x.count), 1);
                    const pct = (d.count / maxD) * 100;
                    return (
                      <div key={d.date} className="flex items-center gap-3" data-testid={`signup-day-${d.date}`}>
                        <span className="text-[10px] text-muted-foreground w-20 shrink-0 font-mono">{d.date}</span>
                        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-400 rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 3)}%` }} />
                        </div>
                        <span className="text-[10px] text-muted-foreground font-mono w-5 text-right">{d.count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No signups in the last 30 days.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
